import React from 'react';
import { BarChart3, Briefcase, Users, Clock, Settings, FileText, X } from 'lucide-react';
import { useSettingsStore } from '../data/settingsStore';
interface SidebarProps {
  isOpen?: boolean;
  onClose?: () => void;
  isAdmin?: boolean;
}
export function Sidebar({
  isOpen = false,
  onClose,
  isAdmin = true
}: SidebarProps) {
  const {
    users
  } = useSettingsStore();
  const currentUser = users.find(user => user.id === 'user-matthew');
  const displayName = currentUser ? `${currentUser.firstName} ${currentUser.lastName}` : 'Your account';
  const initials = currentUser ? `${currentUser.firstName.slice(0, 1)}${currentUser.lastName.slice(0, 1)}`.toUpperCase() : 'A';
  const navItems = [{
    name: 'Jobs',
    icon: Briefcase,
    href: '/jobs'
  }, {
    name: 'Clients',
    icon: Users,
    href: '/clients'
  }, ...(isAdmin ? [{
    name: 'Invoices',
    icon: FileText,
    href: '/invoices'
  }] : []), {
    name: 'Time Sheets',
    icon: Clock,
    href: '/timesheets'
  }, ...(isAdmin ? [{
    name: 'Reporting',
    icon: BarChart3,
    href: '/reporting'
  }, {
    name: 'Settings',
    icon: Settings,
    href: '/settings'
  }] : [])];
  const currentPath = typeof window !== 'undefined' ? window.location.pathname : '/';
  return <>
      {/* Mobile backdrop */}
      {isOpen && <div className="md:hidden fixed inset-0 bg-black/50 z-40" onClick={onClose} aria-hidden="true" data-id="element-404" data-mp="29n5ffebazbbo" />}

      <aside className={`
          w-[260px] flex-shrink-0 bg-[#1F5C9E] text-white flex flex-col h-screen relative overflow-hidden
          fixed md:relative inset-y-0 left-0 z-50
          transform transition-transform duration-300 ease-in-out
          ${isOpen ? 'translate-x-0' : '-translate-x-full'}
          md:translate-x-0
        `} data-id="element-405" data-mp="20ns40g3mccob">
        {/* Background Image Overlay */}
        <div className="absolute inset-0 opacity-20 pointer-events-none mix-blend-overlay" style={{
        backgroundImage: 'url("https://a.storyblok.com/f/312547/6000x4000/39a62afe76/derby-25.jpg/m/1200x0")',
        backgroundSize: 'cover',
        backgroundPosition: 'center'
      }} data-id="element-406" data-mp="2m0im0z39w3om" />

        {/* Content */}
        <div className="relative z-10 flex flex-col h-full" data-id="element-407" data-mp="2399akk9xz4ue">
          {/* Mobile Close Button */}
          <button onClick={onClose} className="md:hidden absolute top-4 right-4 text-white/80 hover:text-white p-1" aria-label="Close menu" data-id="element-408" data-mp="1y9n1mn15care">
            <X className="w-5 h-5" data-id="element-409" data-mp="3snxquwlnl6xp" />
          </button>

          {/* Logo Area */}
          <div className="p-6 flex items-center justify-center" data-id="element-410" data-mp="3sll9olnpz0lp">
            <img src="https://cdn.magicpatterns.com/uploads/x8qyon7Q7FP4Ysx38EhDqB/7b33b6e2-5996-44ed-b635-9846f7d8d920.png" alt="RE Cost Seg" className="h-20 w-auto object-contain" data-id="element-411" data-mp="3omu8a2c80wss" />
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-4 space-y-1 mt-2" data-id="element-412" data-mp="1k5fn73rwf98i">
            {navItems.map(({
            name,
            icon: Icon,
            href
          }) => {
            const isActive = currentPath === href;
            return <a key={name} href={href} onClick={onClose} className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${isActive ? 'bg-white/10 text-white font-medium' : 'text-white/80 hover:text-white hover:bg-white/5'}`} data-id="element-413" data-mp="rgqthpt6a66s">
                  <Icon className="w-5 h-5" data-id="element-414" data-mp="15grt71xpb7ko" />
                  <span className="text-sm" data-id="element-415" data-mp="2bvsor6itdnqq">{name}</span>
                </a>;
          })}
          </nav>

          {/* Bottom Section */}
          <div className="p-4" data-id="element-416" data-mp="2gssnewdc3o7t">
            <a href="/account" onClick={onClose} aria-current={currentPath === '/account' ? 'page' : undefined} className={`group flex items-center gap-3 rounded-xl p-3 transition-colors focus:outline-none focus:ring-2 focus:ring-white/50 ${currentPath === '/account' ? 'bg-white/15' : 'bg-white/10 hover:bg-white/15'}`} data-id="element-417" data-mp="2fdgjb8v86y2y">
              <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center overflow-hidden rounded-full bg-white/15 text-sm font-semibold text-white ring-2 ring-white/20" data-id="element-418" data-mp="2kcb9wwzfpqxm">
                {currentUser?.profileUrl ? <img src={currentUser.profileUrl} alt="" className="h-full w-full object-cover" data-id="element-419" data-mp="2tc7uamesk3mv" /> : initials}
              </span>
              <span className="min-w-0 flex-1 overflow-hidden" data-id="element-420" data-mp="381c8ztmdnpq8">
                <span className="block truncate text-sm font-semibold" data-id="element-421" data-mp="3343qfrxbhjog">{displayName}</span>
                <span className="block truncate text-xs text-white/60" data-id="element-422" data-mp="3h225kzjgpk50">{currentUser?.email || 'Manage your account'}</span>
              </span>
              <span className="text-xs font-medium text-white/0 transition-colors group-hover:text-white/70 group-focus-visible:text-white/70" data-id="element-423" data-mp="3c5jznwcebfr3">Edit</span>
            </a>
          </div>
        </div>
      </aside>
    </>;
}